// 재고관리 PR5(엑셀 일괄등록) 전용 파서·템플릿 유틸.
// /excel(배송엑셀) 코드와 완전히 무관 — templateParser.ts/excelUtils.ts는 읽기 참조만 했고
// 수정하지 않았다. 헤더/샘플 3행 추출은 기존 parseTemplateFile(templateParser.ts)을 재사용하는
// 것이 계약(이 파일은 그 결과를 소비하는 쪽 — suggestInventoryMapping은 headers만 받는다).
// 전체 행 데이터는 templateParser가 샘플 3행만 주므로 이 파일에서 xlsx로 별도 재추출한다.
//
// 매핑/행 계약 단일 진실 = PR5 constraints 문서(T2/T2.5/T3 공유). 요약:
//   - 필드 4종: name(필수) · stock(권장, 미매핑 시 0) · price(선택, 미매핑 시 0) ·
//     threshold(선택, 미매핑 시 5).
//   - ① 키워드 매핑: 헤더 정규화(공백 전부 제거·소문자) 후 사전과 정확히 일치하는 열을 찾는다.
//     필드당 매치가 정확히 1개면 확정, 0개면 unresolved, 2개 이상이면 ambiguous.
//   - 행 계약: name 정규화(normalizeItemName) 결과가 빈 문자열이면 오류 행. 숫자 필드는 빈 셀이면
//     디폴트, 숫자 텍스트/부동소수 오차는 Math.round, 반올림 후 음수 또는 파싱 불가면 오류 행.
//   - 파일 내 중복 정규화명은 duplicates로 보고(행 번호 그룹) — 실행 차단은 UI(T3) 책임.
//   - 유효 행 2000 초과는 capExceeded만 세팅(행 자체는 자르지 않음) — 실행 차단은 UI 책임.
//
// 서버(RPC) 계약: rows[].{name, price, stock, lowStockThreshold}는 정수만 보낸다(T1 RPC가
// 소수를 원문 에러로 거부) — Math.round를 반드시 여기서 적용해 클라가 소수를 절대 내보내지 않는다.

import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { normalizeItemName } from '@/supabase/inventory/normalizeItemName';

// ── 타입 (T3 소비) ──────────────────────────────────────────────────

export type InventoryMappingField = 'name' | 'stock' | 'price' | 'threshold';

export interface InventoryFieldMapping {
  name: number | null;
  stock: number | null;
  price: number | null;
  threshold: number | null;
}

export interface InventoryAmbiguousField {
  field: InventoryMappingField;
  colIdxs: number[];
}

export interface MappingSuggestion {
  mapping: InventoryFieldMapping;
  unresolved: InventoryMappingField[];
  ambiguous: InventoryAmbiguousField[];
}

export interface InventoryExcelRow {
  name: string;
  price: number;
  stock: number;
  lowStockThreshold: number;
  rowNo: number;
}

export interface InventoryExcelRowError {
  rowNo: number;
  reason: string;
}

export interface InventoryExcelDuplicateGroup {
  norm: string;
  rowNos: number[];
}

export interface InventoryExcelParseResult {
  rows: InventoryExcelRow[];
  errors: InventoryExcelRowError[];
  duplicates: InventoryExcelDuplicateGroup[];
  capExceeded: boolean;
  /** 완전 빈 행을 제외한 데이터 행 수(헤더 제외). rows.length + errors.length와 같다. */
  totalDataRows: number;
}

// ── ① 키워드 매핑 ───────────────────────────────────────────────────

const KEYWORD_DICT: Record<InventoryMappingField, string[]> = {
  name: ['상품명', '제품명', '품명', '상품', '제품', '품목', '아이템', '이름', 'name', 'product', 'item'],
  stock: ['현재고', '재고', '수량', '재고수량', '잔여수량', 'stock', 'qty', 'quantity'],
  price: ['판매가', '판매가격', '가격', '단가', '금액', '소비자가', 'price'],
  threshold: ['부족기준', '최소재고', '안전재고', 'threshold'],
};

const MAPPING_FIELDS: InventoryMappingField[] = ['name', 'stock', 'price', 'threshold'];

/** 헤더 정규화: 트림 후 공백 전부 제거, 소문자화(매핑 계약 그대로). */
function normalizeHeaderForMatch(header: string): string {
  return header.trim().replace(/\s+/g, '').toLowerCase();
}

/**
 * 헤더 배열에 키워드 사전을 대조해 name/stock/price/threshold 열을 추정한다.
 * 필드당 매치가 정확히 1개면 확정(mapping에 colIdx), 0개면 unresolved, 2개 이상이면 ambiguous.
 * (name 미해결 또는 어느 필드든 모호 = ① 실패 → 호출부가 ② AI 매핑으로 폴백하는 판단은 T3 책임.)
 */
export function suggestInventoryMapping(headers: string[]): MappingSuggestion {
  const normalizedHeaders = headers.map(normalizeHeaderForMatch);

  const mapping: InventoryFieldMapping = { name: null, stock: null, price: null, threshold: null };
  const unresolved: InventoryMappingField[] = [];
  const ambiguous: InventoryAmbiguousField[] = [];

  for (const field of MAPPING_FIELDS) {
    const keywordSet = new Set(KEYWORD_DICT[field].map(normalizeHeaderForMatch));
    const matchedCols: number[] = [];
    normalizedHeaders.forEach((h, idx) => {
      if (h !== '' && keywordSet.has(h)) matchedCols.push(idx);
    });

    if (matchedCols.length === 1) {
      mapping[field] = matchedCols[0];
    } else if (matchedCols.length === 0) {
      unresolved.push(field);
    } else {
      ambiguous.push({ field, colIdxs: matchedCols });
    }
  }

  return { mapping, unresolved, ambiguous };
}

/**
 * 수동 매핑 확인 UI에서 non-null 필드들끼리 같은 colIdx를 중복 선택했는지 판정(파생 계산,
 * codexloop R1 Fix 2) — 예: 판매가 열을 stock에도 매핑하면 bulkUpsert의 absolute-set 갱신이
 * 재고 값을 가격 값으로 덮어써 데이터가 오염된다. 컨테이너(다음 버튼 disabled)와 스텝(안내
 * 문구) 양쪽이 이 함수를 같은 mapping state로부터 호출해 mirror state 없이 동일한 판정을 공유.
 */
export function hasDuplicateMapping(mapping: InventoryFieldMapping): boolean {
  const selectedColIdxs = MAPPING_FIELDS.map((field) => mapping[field]).filter(
    (colIdx): colIdx is number => colIdx !== null,
  );
  return new Set(selectedColIdxs).size !== selectedColIdxs.length;
}

// ── 행 추출 (전체 데이터, templateParser의 샘플 3행 초과분) ─────────────

function getCell(row: unknown[], idx: number | null): unknown {
  if (idx === null) return '';
  return row[idx] ?? '';
}

interface NumericParseResult {
  value: number;
  error: boolean;
  // true면 error의 원인이 "값이 범위를 초과"(Fix 5) — extractInventoryRows가 필드별 오류
  // 문구를 일반 "값이 올바르지 않습니다"와 구분해 조립할 때 쓴다. error:false면 무의미.
  tooLarge?: boolean;
}

// RPC가 int4 컬럼(price/stock/lowStockThreshold)에 저장하므로 Postgres int4 상한
// (2,147,483,647)을 넘는 값은 서버가 22003(numeric_value_out_of_range) raw 에러로 거부한다 —
// 그 경로는 행을 특정할 수 없는 범용 토스트만 띄운다(codexloop R1 Fix 5). 클라에서 미리 걸러
// rowNo가 있는 오류 행으로 바꾸기 위한 컷오프값 — int4 상한에 여유를 둔 2e9(오타로 나올 법한
// 자릿수 실수를 넉넉히 잡아내면서도 정상 재고/가격 값보다는 훨씬 큰 라운드 넘버).
const MAX_INVENTORY_NUMERIC_VALUE = 2_000_000_000;

/**
 * 숫자 필드 파싱: 빈 셀 = 디폴트, 숫자(문자열 포함)는 Math.round(부동소수 오차/소수 입력 정수화),
 * 반올림 후 음수 또는 파싱 불가(NaN·비수치 텍스트)는 오류, MAX_INVENTORY_NUMERIC_VALUE 초과도
 * 오류(Fix 5, tooLarge 표시).
 * 텍스트 셀의 천단위 콤마(",")는 Number() 이전에 제거한다 — "10,000"처럼 콤마 포맷으로 붙여넣은
 * 가격 셀이 NaN 오류로 떨어지는 것을 막는다. 콤마만 벗기며 그 외 문자(₩/원 등)는 그대로 두므로
 * 진짜 비수치 텍스트("만원" 등)는 여전히 오류 행으로 남는다. 실수치 셀은 이미 number 타입으로
 * 들어오므로 이 처리와 무관.
 * 공백만 있는 텍스트 셀(예: "   ")도 빈 셀과 동일하게 디폴트 처리한다(Fix 3) — trim 순서를
 * 콤마 제거보다 먼저 두어, trim 후 빈 문자열이면 Number('')===0으로 흘러 threshold 디폴트(5)
 * 같은 값이 조용히 0으로 무력화되는 것을 막는다.
 */
function parseNumericCell(raw: unknown, defaultValue: number): NumericParseResult {
  if (raw === '' || raw === null || raw === undefined) {
    return { value: defaultValue, error: false };
  }
  let num: number;
  if (typeof raw === 'number') {
    num = raw;
  } else {
    const trimmed = String(raw).trim();
    if (trimmed === '') {
      return { value: defaultValue, error: false };
    }
    num = Number(trimmed.replace(/,/g, ''));
  }
  if (!Number.isFinite(num)) {
    return { value: 0, error: true };
  }
  const rounded = Math.round(num);
  if (rounded < 0) {
    return { value: 0, error: true };
  }
  if (rounded > MAX_INVENTORY_NUMERIC_VALUE) {
    return { value: 0, error: true, tooLarge: true };
  }
  return { value: rounded, error: false };
}

async function readSheetRows(file: File, sheetIndex: number): Promise<unknown[][]> {
  const arrayBuffer = await file.arrayBuffer();
  // templateParser.ts와 read 옵션이 한 가지만 다르다: blankrows는 true로 읽는다(templateParser는
  // false). sheet_to_json이 blankrows:false면 완전 빈 행을 배열에서 통째로 드롭해 그 뒤 모든
  // 인덱스가 한 칸씩 당겨지고, extractInventoryRows가 그 인덱스로 rowNo(=물리 엑셀 행 번호)를
  // 계산하면 데이터 본문 중간의 빈 행 하나로도 그 뒤 모든 오류/중복 rowNo가 실제 엑셀 행과
  // 어긋난다(셀러가 미리보기에서 대조할 수 없게 됨 — 계약 위반). blankrows:true는 빈 행도
  // defval로 채운 배열(예: ['','','']')로 유지해 인덱스=물리 행 오프셋이 보존되고, 그 빈 행을
  // 데이터에서 제외하는 것은 extractInventoryRows가 명시적으로 스킵하는 방식으로 처리한다.
  // header/defval 옵션은 templateParser.ts와 동일하게 유지(컬럼 인덱스 정렬 계약 보존).
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const sheetName = workbook.SheetNames[sheetIndex];
  if (!sheetName) return [];

  const worksheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    header: 1,
    defval: '',
    blankrows: true,
  });
}

/**
 * jsonData(blankrows:true, defval='')에서 "첫 번째 완전히-비어있지 않은 행"의 인덱스를 찾는다.
 * templateParser.ts(blankrows:false)가 헤더로 취급하는 행과 판정 기준을 일치시키기 위함(F2) —
 * blankrows:false는 완전 빈 행(스타일만 있고 값이 없는 선행 행 포함)을 배열에서 통째로 드롭하므로
 * jsonData[0]이 곧 "첫 비어있지 않은 행"이 된다. 여기서도 동일한 isBlankRow 판정(모든 셀이
 * String().trim() 후 빈 문자열)으로 그 행을 찾는다. 전부 빈 시트는 0을 반환(빈 데이터로 자연 처리).
 */
function findHeaderRowIndex(jsonData: unknown[][]): number {
  for (let i = 0; i < jsonData.length; i++) {
    const row = jsonData[i] ?? [];
    const isBlank = (row as unknown[]).every((cell) => String(cell ?? '').trim() === '');
    if (!isBlank) return i;
  }
  return 0;
}

/**
 * 매핑을 적용해 시트 전체 데이터 행을 추출한다. 헤더 행은 "첫 번째 완전히 비어있지 않은 행"으로
 * 판정한다(findHeaderRowIndex, F2) — templateParser.ts(blankrows:false, 매핑 UI가 보여주는 헤더)와
 * 판정 기준을 일치시켜, 시트 dimension 안 스타일만-빈 선행 행이 있어도 매핑 UI가 본 헤더 행과
 * 여기서 스킵하는 헤더 행이 어긋나지 않게 한다(어긋나면 실헤더 행이 데이터로 처리돼 헤더 텍스트가
 * 유령 아이템으로 저장될 수 있었음). rowNo는 실제 엑셀 물리 행 번호(1-based) — 데이터 본문
 * 중간에 완전히 빈 행이 끼어 있어도, 또는 선행에 빈 행이 있어 헤더가 물리 2행 이상이어도,
 * 그 뒤 행들의 rowNo는 물리 위치 그대로 보고된다(readSheetRows가 blankrows:true로 읽어 인덱스를
 * 보존하기 때문. Finding 1). 정상 파일(헤더=물리 1행, headerRowIdx=0)은 rowNo 계산이 기존과
 * byte-동일(headerRowIdx + idx + 2 = idx + 2).
 * 완전 빈 행(모든 셀이 String().trim() 후 빈 문자열)은 데이터에서 제외하되 rowNo 카운팅에서는
 * 스킵만 하고 인덱스 자체는 건드리지 않는다.
 * name 미매핑(mapping.name === null)이면 모든 (빈 행이 아닌) 행이 빈 이름 오류로 처리된다
 * (방어적 폴백 — UI는 정상 흐름에서 name 미매핑 상태로 이 함수를 호출하지 않는다).
 */
export async function extractInventoryRows(
  file: File,
  sheetIndex: number,
  mapping: InventoryFieldMapping,
): Promise<InventoryExcelParseResult> {
  const jsonData = await readSheetRows(file, sheetIndex);
  const headerRowIdx = findHeaderRowIndex(jsonData);
  const dataRows = jsonData.slice(headerRowIdx + 1);

  const rows: InventoryExcelRow[] = [];
  const errors: InventoryExcelRowError[] = [];
  const normToRowNos = new Map<string, number[]>();
  // totalDataRows = 완전 빈 행을 제외한 데이터 행 수(기존 blankrows:false 시절과 동일한 의미를
  // blankrows:true 아래서도 유지) — 아래 루프에서 빈 행을 만날 때마다 카운트하지 않고 skip한다.
  let totalDataRows = 0;

  dataRows.forEach((row, idx) => {
    const rowNo = headerRowIdx + idx + 2;
    const safeRow = row ?? [];

    const isBlankRow = safeRow.every((cell) => String(cell ?? '').trim() === '');
    if (isBlankRow) {
      return; // 물리 행 카운팅(rowNo)에는 반영되지만 데이터/totalDataRows에서는 제외
    }
    totalDataRows += 1;

    const rawName = getCell(safeRow, mapping.name);
    // .trim(): src/supabase/inventory/service/inventoryService.ts의 클라-트림 컨벤션(RPC가 name을
    // 원문 그대로 저장하므로 트림은 클라 책임)과 맞춘다(F3) — 트림 생략 시 " 사과 " 같은 앞뒤
    // 공백이 verbatim 저장돼 신규 생성 시 지저분한 표시명, 갱신 시 기존 깨끗한 표시명을 클로버한다.
    // normalizeItemName은 내부 공백까지 전부 제거하므로(빈 판정) trim 유무와 무관하게 norm 결과는
    // 동일 — 순서 정합 문제 없음.
    const name = String(rawName ?? '').trim();
    const norm = normalizeItemName(name);

    if (norm === '') {
      errors.push({ rowNo, reason: '상품명이 비어 있습니다.' });
      return;
    }

    const stockResult = parseNumericCell(getCell(safeRow, mapping.stock), 0);
    if (stockResult.error) {
      errors.push({ rowNo, reason: stockResult.tooLarge ? '현재고 값이 너무 큽니다.' : '현재고 값이 올바르지 않습니다.' });
      return;
    }

    const priceResult = parseNumericCell(getCell(safeRow, mapping.price), 0);
    if (priceResult.error) {
      errors.push({ rowNo, reason: priceResult.tooLarge ? '판매가 값이 너무 큽니다.' : '판매가 값이 올바르지 않습니다.' });
      return;
    }

    const thresholdResult = parseNumericCell(getCell(safeRow, mapping.threshold), 5);
    if (thresholdResult.error) {
      errors.push({
        rowNo,
        reason: thresholdResult.tooLarge ? '부족 기준 값이 너무 큽니다.' : '부족 기준 값이 올바르지 않습니다.',
      });
      return;
    }

    rows.push({
      name,
      price: priceResult.value,
      stock: stockResult.value,
      lowStockThreshold: thresholdResult.value,
      rowNo,
    });

    const existing = normToRowNos.get(norm);
    if (existing) {
      existing.push(rowNo);
    } else {
      normToRowNos.set(norm, [rowNo]);
    }
  });

  const duplicates: InventoryExcelDuplicateGroup[] = [];
  normToRowNos.forEach((rowNos, norm) => {
    if (rowNos.length > 1) duplicates.push({ norm, rowNos });
  });

  const capExceeded = rows.length > 2000;

  return { rows, errors, duplicates, capExceeded, totalDataRows };
}

// ── 템플릿 다운로드 ─────────────────────────────────────────────────

/**
 * 백지에서 시작하는 셀러용 재고 일괄등록 템플릿(.xlsx) 다운로드.
 * 헤더: 상품명 | 판매가 | 현재고 | 부족 기준. 우리 템플릿이므로 ①에서 4/4 확정 매핑된다.
 */
export async function downloadInventoryTemplate(): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Seller-Helper';
  workbook.lastModifiedBy = 'Seller-Helper';
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet('재고 일괄등록', {
    pageSetup: { paperSize: 9, orientation: 'portrait' },
  });

  worksheet.columns = [
    { header: '상품명', key: 'name', width: 30 },
    { header: '판매가', key: 'price', width: 15 },
    { header: '현재고', key: 'stock', width: 15 },
    { header: '부족 기준', key: 'threshold', width: 15 },
  ];

  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '15B8A6' },
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      bold: true,
      size: 11,
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center',
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  const exampleRow = worksheet.addRow({ name: '예시상품', price: 10000, stock: 50, threshold: 5 });
  exampleRow.eachCell((cell) => {
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  worksheet.views = [{ state: 'frozen', ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = '재고_일괄등록_템플릿.xlsx';
  link.click();

  window.URL.revokeObjectURL(url);
}
